import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import Toast from '../../components/ui/Toast';
import { knowledgeHubService } from '../../services/knowledgeHubService';
import { format, formatDistanceToNow } from 'date-fns';

const EditArticle = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('editor'); // 'editor' | 'history'
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [originalData, setOriginalData] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    content_type: 'article',
    status: 'draft',
    author: 'Northstar Analytics',
    author_role: 'Data Strategy Experts',
    author_image: '',
    category: '',
    tags: [],
    image_url: '',
    video_url: '',
    read_time: '',
    publish_date: new Date()?.toISOString()?.split('T')?.[0],
    is_featured: false
  });

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    if (userProfile?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    if (id) {
      loadArticle();
      loadVersions();
    }
  }, [user, userProfile, id]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      const data = await knowledgeHubService?.getContentById(id);
      if (!data) {
        setToast({ message: 'Article not found', type: 'error' });
        setTimeout(() => navigate('/knowledge-hub-admin'), 1500);
        return;
      }
      const mapped = {
        title: data?.title || '',
        slug: data?.slug || '',
        excerpt: data?.excerpt || '',
        content: data?.content || '',
        content_type: data?.content_type || 'article',
        status: data?.status || 'draft',
        author: data?.author || 'Northstar Analytics',
        author_role: data?.author_role || 'Data Strategy Experts',
        author_image: data?.author_image || '',
        category: data?.category || '',
        tags: data?.tags || [],
        image_url: data?.image_url || '',
        video_url: data?.video_url || '',
        read_time: data?.read_time || '',
        publish_date: data?.publish_date
          ? new Date(data?.publish_date)?.toISOString()?.split('T')?.[0]
          : new Date()?.toISOString()?.split('T')?.[0],
        is_featured: data?.is_featured || false
      };
      setFormData(mapped);
      setOriginalData(mapped);
    } catch (err) {
      setToast({ message: err?.message || 'Failed to load article', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadVersions = async () => {
    try {
      setVersionsLoading(true);
      const data = await knowledgeHubService?.getArticleVersions(id);
      setVersions(data || []);
    } catch (err) {
      // Versions table may not exist yet — silently ignore
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setIsDirty(true);
  };

  const handleTagsChange = (e) => {
    const tagsArray = e?.target?.value?.split(',')?.map(tag => tag?.trim())?.filter(tag => tag);
    setFormData(prev => ({ ...prev, tags: tagsArray }));
    setIsDirty(true);
  };

  const handleSave = async (publishStatus) => {
    if (!formData?.title?.trim()) {
      setToast({ message: 'Title is required', type: 'error' });
      return;
    }
    if (!formData?.content?.trim()) {
      setToast({ message: 'Content is required', type: 'error' });
      return;
    }

    try {
      setSaving(true);
      const dataToSave = { ...formData, status: publishStatus };

      // Save a version snapshot before updating
      await knowledgeHubService?.saveArticleVersion(id, originalData, publishStatus);

      await knowledgeHubService?.updateContent(id, dataToSave);

      setOriginalData(dataToSave);
      setIsDirty(false);

      const msg = publishStatus === 'published' ?'Article published successfully!'
        : publishStatus === 'draft' ?'Article saved as draft' :'Article archived';

      setToast({ message: msg, type: 'success' });
      await loadVersions();
    } catch (err) {
      setToast({ message: err?.message || 'Failed to save article', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreVersion = async (version) => {
    if (!window.confirm('Restore this version? Your current unsaved changes will be replaced.')) return;
    try {
      setSaving(true);
      const snapshot = version?.snapshot || {};
      const restored = {
        title: snapshot?.title || '',
        slug: snapshot?.slug || '',
        excerpt: snapshot?.excerpt || '',
        content: snapshot?.content || '',
        content_type: snapshot?.content_type || 'article',
        status: snapshot?.status || 'draft',
        author: snapshot?.author || 'Northstar Analytics',
        author_role: snapshot?.author_role || 'Data Strategy Experts',
        author_image: snapshot?.author_image || '',
        category: snapshot?.category || '',
        tags: snapshot?.tags || [],
        image_url: snapshot?.image_url || '',
        video_url: snapshot?.video_url || '',
        read_time: snapshot?.read_time || '',
        publish_date: snapshot?.publish_date || new Date()?.toISOString()?.split('T')?.[0],
        is_featured: snapshot?.is_featured || false
      };
      await knowledgeHubService?.updateContent(id, restored);
      setFormData(restored);
      setOriginalData(restored);
      setIsDirty(false);
      setSelectedVersion(null);
      setActiveTab('editor');
      setToast({ message: 'Version restored successfully', type: 'success' });
      await loadVersions();
    } catch (err) {
      setToast({ message: err?.message || 'Failed to restore version', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const wordCount = formData?.content?.trim()?.split(/\s+/)?.filter(w => w)?.length || 0;
  const estimatedReadTime = Math.max(1, Math.ceil(wordCount / 200));

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700 border-gray-200',
      published: 'bg-[#D1F5F0] text-[#1FB5A5] border-[#1FB5A5]/30',
      archived: 'bg-yellow-100 text-yellow-700 border-yellow-200'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading article...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Edit Article | Knowledge Hub Admin</title>
      </Helmet>
      <Header />

      <main className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/knowledge-hub-admin')}
                className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors"
              >
                <Icon name="ArrowLeft" size={18} />
                <span className="text-sm font-medium">Back to Admin</span>
              </button>
              <div className="h-5 w-px bg-gray-300" />
              <div>
                <div className="flex items-center space-x-2">
                  <h1 className="text-2xl font-bold text-primary">Edit Article</h1>
                  {isDirty && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                      Unsaved changes
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-0.5 truncate max-w-xs">{formData?.title || 'Untitled'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => handleSave('draft')}
                disabled={saving}
                iconName="Save"
                iconPosition="left"
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </Button>
              <Button
                onClick={() => handleSave('published')}
                disabled={saving}
                iconName="Send"
                iconPosition="left"
              >
                {saving ? 'Publishing...' : 'Publish'}
              </Button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center space-x-1 mb-6 bg-white rounded-xl shadow-elevation-1 p-1 w-fit">
            <button
              onClick={() => setActiveTab('editor')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'editor' ?'bg-primary text-white' :'text-muted-foreground hover:text-foreground hover:bg-gray-100'
              }`}
            >
              <Icon name="Edit3" size={15} />
              <span>Editor</span>
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'history' ?'bg-primary text-white' :'text-muted-foreground hover:text-foreground hover:bg-gray-100'
              }`}
            >
              <Icon name="History" size={15} />
              <span>Version History</span>
              {versions?.length > 0 && (
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${
                  activeTab === 'history' ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary'
                }`}>
                  {versions?.length}
                </span>
              )}
            </button>
          </div>

          {/* EDITOR TAB */}
          {activeTab === 'editor' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6">

                {/* Title & Slug */}
                <div className="bg-white rounded-xl shadow-elevation-1 p-6">
                  <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Article Details</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        name="title"
                        value={formData?.title}
                        onChange={handleInputChange}
                        placeholder="Enter a compelling article title..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">URL Slug</label>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-muted-foreground whitespace-nowrap">/article/</span>
                        <Input
                          type="text"
                          name="slug"
                          value={formData?.slug}
                          onChange={handleInputChange}
                          placeholder="url-friendly-slug"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Excerpt</label>
                      <textarea
                        name="excerpt"
                        value={formData?.excerpt}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm resize-none"
                        placeholder="A brief summary that appears in article listings..."
                      />
                    </div>
                  </div>
                </div>

                {/* Content Editor */}
                <div className="bg-white rounded-xl shadow-elevation-1 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Content <span className="text-red-500">*</span>
                    </h2>
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span>{wordCount} words</span>
                      <span>~{estimatedReadTime} min read</span>
                    </div>
                  </div>
                  <textarea
                    name="content"
                    value={formData?.content}
                    onChange={handleInputChange}
                    rows={22}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm resize-y"
                    placeholder="Write your article content here. Supports markdown formatting."
                  />
                  <p className="text-xs text-muted-foreground mt-2">Supports Markdown formatting</p>
                </div>

                {/* Author */}
                <div className="bg-white rounded-xl shadow-elevation-1 p-6">
                  <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Author</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Name</label>
                      <Input
                        type="text"
                        name="author"
                        value={formData?.author}
                        onChange={handleInputChange}
                        placeholder="Author name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Role</label>
                      <Input
                        type="text"
                        name="author_role"
                        value={formData?.author_role}
                        onChange={handleInputChange}
                        placeholder="e.g., Data Strategy Expert"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">

                {/* Publish Settings */}
                <div className="bg-white rounded-xl shadow-elevation-1 p-6">
                  <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Publish Settings</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                      <Select name="status" value={formData?.status} onChange={handleInputChange}>
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="archived">Archived</option>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Content Type</label>
                      <Select name="content_type" value={formData?.content_type} onChange={handleInputChange}>
                        <option value="article">Article</option>
                        <option value="video">Video</option>
                        <option value="newsletter">Newsletter</option>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Publish Date</label>
                      <Input
                        type="date"
                        name="publish_date"
                        value={formData?.publish_date}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="is_featured"
                          checked={formData?.is_featured}
                          onChange={handleInputChange}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-sm font-medium text-foreground">Featured Content</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Category & Tags */}
                <div className="bg-white rounded-xl shadow-elevation-1 p-6">
                  <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Categorisation</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Category</label>
                      <Input
                        type="text"
                        name="category"
                        value={formData?.category}
                        onChange={handleInputChange}
                        placeholder="e.g., Data Governance"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Tags</label>
                      <Input
                        type="text"
                        value={formData?.tags?.join(', ')}
                        onChange={handleTagsChange}
                        placeholder="Comma-separated tags"
                      />
                      {formData?.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {formData?.tags?.map((tag, i) => (
                            <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary font-medium">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Media */}
                <div className="bg-white rounded-xl shadow-elevation-1 p-6">
                  <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Media</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Cover Image URL</label>
                      <Input
                        type="url"
                        name="image_url"
                        value={formData?.image_url}
                        onChange={handleInputChange}
                        placeholder="https://example.com/image.jpg"
                      />
                      {formData?.image_url && (
                        <img
                          src={formData?.image_url}
                          alt="Cover preview"
                          className="mt-2 w-full h-28 object-cover rounded-lg border border-gray-200"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                    </div>
                    {formData?.content_type === 'video' && (
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Video URL</label>
                        <Input
                          type="url"
                          name="video_url"
                          value={formData?.video_url}
                          onChange={handleInputChange}
                          placeholder="https://youtube.com/watch?v=..."
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Read Time</label>
                      <Input
                        type="text"
                        name="read_time"
                        value={formData?.read_time}
                        onChange={handleInputChange}
                        placeholder={`~${estimatedReadTime} min read`}
                      />
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-xl shadow-elevation-1 p-6">
                  <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Quick Actions</h2>
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      onClick={() => handleSave('archived')}
                      disabled={saving}
                      iconName="Archive"
                      iconPosition="left"
                      className="w-full justify-start"
                    >
                      Archive Article
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => navigate('/knowledge-hub-admin')}
                      iconName="X"
                      iconPosition="left"
                      className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      Discard Changes
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* VERSION HISTORY TAB */}
          {activeTab === 'history' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

              {/* Version List */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-elevation-1 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100">
                    <h2 className="text-sm font-semibold text-foreground">Publish History</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">{versions?.length} version{versions?.length !== 1 ? 's' : ''} saved</p>
                  </div>

                  {versionsLoading ? (
                    <div className="p-8 text-center">
                      <Icon name="Loader" size={24} className="text-primary animate-spin mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Loading history...</p>
                    </div>
                  ) : versions?.length === 0 ? (
                    <div className="p-8 text-center">
                      <Icon name="History" size={32} className="text-muted-foreground mx-auto mb-3" />
                      <p className="text-sm font-medium text-foreground mb-1">No versions yet</p>
                      <p className="text-xs text-muted-foreground">Save the article to create the first version snapshot.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                      {versions?.map((version, index) => (
                        <button
                          key={version?.id}
                          onClick={() => setSelectedVersion(selectedVersion?.id === version?.id ? null : version)}
                          className={`w-full text-left px-6 py-4 hover:bg-gray-50 transition-colors ${
                            selectedVersion?.id === version?.id ? 'bg-primary/5 border-l-2 border-primary' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="text-xs font-semibold text-foreground">
                                  v{versions?.length - index}
                                </span>
                                {index === 0 && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                                    Latest
                                  </span>
                                )}
                              </div>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(version?.status_at_save)}`}>
                                {version?.status_at_save?.charAt(0)?.toUpperCase() + version?.status_at_save?.slice(1)}
                              </span>
                              <p className="text-xs text-muted-foreground mt-1.5">
                                {version?.saved_at
                                  ? formatDistanceToNow(new Date(version?.saved_at), { addSuffix: true })
                                  : 'Unknown time'}
                              </p>
                              {version?.saved_at && (
                                <p className="text-xs text-muted-foreground">
                                  {format(new Date(version?.saved_at), 'MMM dd, yyyy HH:mm')}
                                </p>
                              )}
                            </div>
                            <Icon
                              name={selectedVersion?.id === version?.id ? 'ChevronUp' : 'ChevronDown'}
                              size={14}
                              className="text-muted-foreground mt-1 flex-shrink-0"
                            />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Version Detail / Diff Preview */}
              <div className="lg:col-span-2">
                {selectedVersion ? (
                  <div className="bg-white rounded-xl shadow-elevation-1 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                      <div>
                        <h2 className="text-sm font-semibold text-foreground">
                          Version snapshot — {selectedVersion?.saved_at
                            ? format(new Date(selectedVersion?.saved_at), 'MMM dd, yyyy HH:mm')
                            : 'Unknown'}
                        </h2>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Status at save: <span className="font-medium capitalize">{selectedVersion?.status_at_save}</span>
                        </p>
                      </div>
                      <Button
                        onClick={() => handleRestoreVersion(selectedVersion)}
                        disabled={saving}
                        iconName="RotateCcw"
                        iconPosition="left"
                        size="sm"
                      >
                        Restore This Version
                      </Button>
                    </div>

                    <div className="p-6 space-y-5 max-h-[600px] overflow-y-auto">
                      {/* Title */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Title</p>
                        <p className="text-sm text-foreground font-medium">{selectedVersion?.snapshot?.title || '—'}</p>
                      </div>

                      {/* Status & Category */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Category</p>
                          <p className="text-sm text-foreground">{selectedVersion?.snapshot?.category || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Author</p>
                          <p className="text-sm text-foreground">{selectedVersion?.snapshot?.author || '—'}</p>
                        </div>
                      </div>

                      {/* Excerpt */}
                      {selectedVersion?.snapshot?.excerpt && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Excerpt</p>
                          <p className="text-sm text-muted-foreground">{selectedVersion?.snapshot?.excerpt}</p>
                        </div>
                      )}

                      {/* Tags */}
                      {selectedVersion?.snapshot?.tags?.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Tags</p>
                          <div className="flex flex-wrap gap-1">
                            {selectedVersion?.snapshot?.tags?.map((tag, i) => (
                              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700 font-medium">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Content Preview */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Content Preview</p>
                        <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                          <pre className="text-xs text-foreground font-mono whitespace-pre-wrap leading-relaxed">
                            {selectedVersion?.snapshot?.content
                              ? selectedVersion?.snapshot?.content?.slice(0, 1500) + (selectedVersion?.snapshot?.content?.length > 1500 ? '\n\n[...truncated]' : '')
                              : '—'}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-elevation-1 p-12 text-center">
                    <Icon name="MousePointerClick" size={40} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-base font-semibold text-foreground mb-2">Select a version</h3>
                    <p className="text-sm text-muted-foreground">
                      Click a version from the list to preview its content and restore it if needed.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default EditArticle;
